import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../components/Layout/LanguageContext';
import { Link } from 'react-router-dom';
import {
  FaArrowLeft,
  FaHandshake,
  FaLaptopCode,
  FaRocket,
  FaCheckCircle,
  FaFileSignature,
  FaCogs,
  FaEuroSign,
  FaMapMarkedAlt,
  FaInfoCircle,
  FaExternalLinkAlt,
  FaBalanceScale
} from 'react-icons/fa';

const FundingSection = styled.section`
  padding: 6rem 2rem;
  background: var(--background-color);
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 2rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: var(--background-light);
    transform: translateX(-4px);
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    margin-bottom: 2.5rem;
  }
`;

const SectionTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2.25rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--text-secondary);
  max-width: 760px;
  margin: 0 auto;
  line-height: 1.6;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const Card = styled(motion.div)<{ $featured?: boolean }>`
  background: var(--background-light);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 8px 32px rgba(0, 51, 102, 0.1);
  border: 1px solid ${({ $featured }) => ($featured ? 'var(--accent-color)' : 'var(--background-dark)')};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 48px rgba(0, 51, 102, 0.15);
    border-color: var(--accent-color);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--primary-color), var(--accent-color));
    transform: scaleX(${({ $featured }) => ($featured ? 1 : 0)});
    transition: transform 0.3s ease;
  }

  &:hover::before {
    transform: scaleX(1);
  }

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const CardIcon = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
  font-size: 1.75rem;
  color: white;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
`;

const Amount = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: var(--accent-dark);
  line-height: 1.1;
  margin-bottom: 0.25rem;
`;

const AmountLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary);
  margin-bottom: 1rem;
`;

const CardTitle = styled.h2`
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.75rem;
  line-height: 1.3;
`;

const CardText = styled.p`
  font-size: 0.98rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const FactList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 0.5rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: auto;
`;

const FactItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.92rem;
  color: var(--text-color);
  line-height: 1.45;

  svg {
    color: var(--success);
    flex-shrink: 0;
    margin-top: 0.2rem;
  }
`;

const Note = styled.p`
  font-size: 0.82rem;
  color: var(--text-secondary);
  font-style: italic;
  margin: 1rem 0 0 0;
`;

const Band = styled.div`
  background: var(--background-light);
  border: 1px solid var(--background-dark);
  border-radius: 16px;
  padding: 2.5rem;
  margin-top: 3rem;
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 2rem;
    gap: 1.25rem;
  }
`;

const BandIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  color: white;
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
`;

const BandBody = styled.div`
  flex: 1;
`;

const BandTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--primary-color);
  margin: 0 0 0.5rem 0;
`;

const BandText = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
`;

const OutlineLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  background: transparent;
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
  padding: 0.75rem 1.4rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: var(--primary-color);
    color: white;
    transform: translateY(-2px);
  }
`;

const StepsHeader = styled.h2`
  text-align: center;
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-color);
  margin: 4rem 0 0 0;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 2.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const Step = styled.div`
  text-align: center;
  padding: 1rem;
`;

const StepIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin: 0 auto 1.25rem auto;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--primary-color);
  background: var(--background-dark);
`;

const StepTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const StepText = styled.p`
  font-size: 0.95rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
`;

const CTA = styled.div`
  text-align: center;
  margin-top: 4rem;
  padding: 3rem 2rem;
  border-radius: 20px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  color: white;
`;

const CTATitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0 0 1.5rem 0;
  color: white;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--accent-color);
  color: var(--primary-dark);
  padding: 0.9rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.05rem;
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent-light);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  }
`;

const Disclaimer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  max-width: 900px;
  margin: 3rem auto 0 auto;
  padding: 1.25rem 1.5rem;
  border-radius: 12px;
  background: var(--background-dark);
  color: var(--text-secondary);
  font-size: 0.85rem;
  line-height: 1.6;

  svg {
    flex-shrink: 0;
    margin-top: 0.15rem;
    color: var(--info);
  }
`;

const EitherOr = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  max-width: 980px;
  margin: 2.5rem auto 0 auto;
  padding: 1.5rem 1.75rem;
  border-radius: 14px;
  background: rgba(255, 180, 0, 0.1);
  border: 1px solid var(--accent-color);

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const EitherOrIcon = styled.div`
  font-size: 1.5rem;
  color: var(--accent-dark);
  flex-shrink: 0;
  margin-top: 0.1rem;
`;

const EitherOrTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--primary-color);
  margin: 0 0 0.4rem 0;
`;

const EitherOrText = styled.p`
  font-size: 0.95rem;
  color: var(--text-color);
  line-height: 1.6;
  margin: 0;
`;

interface Program {
  amount: string;
  amountLabel: string;
  title: string;
  text: string;
  facts: string[];
  note?: string;
}

interface FundingStrings {
  backButton: string;
  title: string;
  subtitle: string;
  programs: Program[];
  eitherOr: {
    title: string;
    text: string;
  };
  otherStates: {
    title: string;
    text: string;
    linkText: string;
  };
  stepsTitle: string;
  steps: { title: string; text: string }[];
  ctaTitle: string;
  ctaButton: string;
  disclaimer: string;
}

const fundingContent: { de: FundingStrings; en: FundingStrings } = {
  de: {
    backButton: 'Zurück zur Startseite',
    title: 'Förderung für Ihr Projekt',
    subtitle:
      'Beratungs- und Digitalisierungsleistungen werden vom Bund und vom Freistaat Bayern bezuschusst. So lässt sich ein erheblicher Teil Ihrer Investition fördern – gerne stimme ich mein Leistungsangebot auf das passende Programm ab.',
    programs: [
      {
        amount: 'bis zu 1.750 €',
        amountLabel: 'BAFA-Beratungsförderung',
        title: 'Förderung von Unternehmensberatungen für KMU',
        text: 'Der Bund bezuschusst konzeptionelle Beratungen zu wirtschaftlichen, organisatorischen und digitalen Fragen der Unternehmensführung – für kleine und mittlere Unternehmen sowie Freie Berufe.',
        facts: [
          'Fördersatz in Bayern: 50 % der Beratungskosten – bis zu 1.750 €',
          'In den neuen Bundesländern bis zu 80 % bzw. 2.800 €',
          'Zielgruppe: KMU & Freie Berufe mit Sitz in Deutschland',
          'Laufzeit des Programms: bis 31.12.2026',
          'Wichtig: Antrag vor Auftragsvergabe, Beratung durch BAFA-registrierten Berater'
        ]
      },
      {
        amount: 'bis zu 7.500 €',
        amountLabel: 'Digitalbonus Bayern – Standard',
        title: 'Digitalisierung von Prozessen & Software',
        text: 'Der Freistaat Bayern fördert die Digitalisierung Ihrer Produkte, Prozesse und Dienstleistungen – etwa neue Software, Datenanalyse oder Prozessautomatisierung.',
        facts: [
          'Fördersatz: bis zu 50 % – maximal 7.500 €',
          'Zielgruppe: kleine Unternehmen mit Betriebsstätte in Bayern (< 50 Mitarbeitende)',
          'Laufzeit des Programms: bis 31.12.2027',
          'Voraussetzung: ELSTER-Unternehmenskonto, Antrag vor Auftragsvergabe'
        ]
      },
      {
        amount: 'bis zu 30.000 €',
        amountLabel: 'Digitalbonus Bayern – Plus',
        title: 'Besonders innovative Vorhaben',
        text: 'Für umfangreiche Digitalisierungsvorhaben mit besonderem Innovationspotenzial und hohem Neuheitsgrad bietet Bayern eine deutlich erhöhte Förderung.',
        facts: [
          'Fördersatz: bis zu 50 % – maximal 30.000 €',
          'Zielgruppe: wie Standard, bei besonderem Innovationspotenzial',
          'Laufzeit des Programms: bis 31.12.2027',
          'Nachweis des Innovationspotenzials erforderlich'
        ],
        note: 'Der Digitalbonus Plus kann nur einmal je Fördergebiet in Anspruch genommen werden.'
      }
    ],
    eitherOr: {
      title: 'Wichtig: pro Vorhaben gilt „entweder – oder"',
      text: 'Für ein und dieselbe Maßnahme kann immer nur ein öffentlicher Zuschuss genutzt werden – BAFA-Förderung und Digitalbonus lassen sich für dasselbe Projekt nicht kombinieren (Kumulierungsverbot). Die Programme haben unterschiedliche Schwerpunkte: Die BAFA-Förderung bezuschusst die Beratung, der Digitalbonus die Umsetzung. Gerne finde ich gemeinsam mit Ihnen das passende Programm für Ihr Vorhaben.'
    },
    otherStates: {
      title: 'Sie sind nicht in Bayern?',
      text: 'Auch andere Bundesländer fördern Digitalisierungsvorhaben – etwa Nordrhein-Westfalen, Sachsen, Hamburg oder Thüringen – mit eigenen Programmen und unterschiedlichen Förderhöhen. Eine stets aktuelle Übersicht aller Programme von Bund und Ländern finden Sie in der Förderdatenbank des Bundes.',
      linkText: 'Zur Förderdatenbank'
    },
    stepsTitle: 'So läuft die Förderung ab',
    steps: [
      {
        title: '1. Antrag stellen',
        text: 'Zuerst wird der Förderantrag gestellt – immer vor Auftragsvergabe bzw. Vertragsabschluss. Eine rückwirkende Förderung ist ausgeschlossen.'
      },
      {
        title: '2. Projekt umsetzen',
        text: 'Nach der Bewilligung setzen wir Ihr Beratungs- oder Digitalisierungsprojekt gemeinsam um.'
      },
      {
        title: '3. Zuschuss erhalten',
        text: 'Nach Abschluss und Verwendungsnachweis wird der Zuschuss von der zuständigen Stelle ausgezahlt.'
      }
    ],
    ctaTitle: 'Lassen Sie uns Ihr förderfähiges Projekt besprechen',
    ctaButton: 'Beratung anfragen',
    disclaimer:
      'Stand: Juni 2026. Alle Angaben ohne Gewähr; sie ersetzen keine Förder- oder Rechtsberatung. Förderhöhen, Voraussetzungen und Fristen können sich ändern, die Förderfähigkeit wird im Einzelfall durch die zuständige Stelle entschieden. Eine Kombination mehrerer öffentlicher Zuschüsse für dieselbe Maßnahme ist in der Regel ausgeschlossen.'
  },
  en: {
    backButton: 'Back to Homepage',
    title: 'Funding for Your Project',
    subtitle:
      'Consulting and digitalization services are subsidized by the German federal government and the Free State of Bavaria. This means a significant part of your investment can be funded – I am happy to align my services with the right programme.',
    programs: [
      {
        amount: 'up to €1,750',
        amountLabel: 'BAFA Consulting Grant',
        title: 'Funding of Business Consulting for SMEs',
        text: 'The federal government subsidizes conceptual consulting on economic, organizational and digital questions of business management – for small and medium-sized enterprises and the liberal professions.',
        facts: [
          'Funding rate in Bavaria: 50% of consulting costs – up to €1,750',
          'In the new federal states up to 80% or €2,800',
          'Target group: SMEs & liberal professions based in Germany',
          'Programme runs until 31 Dec 2026',
          'Important: apply before commissioning, consulting by a BAFA-registered advisor'
        ]
      },
      {
        amount: 'up to €7,500',
        amountLabel: 'Digitalbonus Bayern – Standard',
        title: 'Digitalization of Processes & Software',
        text: 'The Free State of Bavaria funds the digitalization of your products, processes and services – such as new software, data analytics or process automation.',
        facts: [
          'Funding rate: up to 50% – maximum €7,500',
          'Target group: small companies with a place of business in Bavaria (< 50 employees)',
          'Programme runs until 31 Dec 2027',
          'Requirement: ELSTER business account, apply before commissioning'
        ]
      },
      {
        amount: 'up to €30,000',
        amountLabel: 'Digitalbonus Bayern – Plus',
        title: 'Particularly Innovative Projects',
        text: 'For extensive digitalization projects with particular innovation potential and a high degree of novelty, Bavaria offers significantly higher funding.',
        facts: [
          'Funding rate: up to 50% – maximum €30,000',
          'Target group: as Standard, with particular innovation potential',
          'Programme runs until 31 Dec 2027',
          'Proof of innovation potential required'
        ],
        note: 'The Digitalbonus Plus can only be claimed once per funding area.'
      }
    ],
    eitherOr: {
      title: 'Important: one programme per project',
      text: 'For one and the same measure, only a single public grant can be used – the BAFA grant and the Digitalbonus cannot be combined for the same project (no cumulation). The programmes have different focuses: the BAFA grant subsidizes the consulting, the Digitalbonus the implementation. I am happy to help you find the right programme for your project.'
    },
    otherStates: {
      title: 'Not located in Bavaria?',
      text: 'Other German federal states also fund digitalization projects – for example North Rhine-Westphalia, Saxony, Hamburg or Thuringia – with their own programmes and varying funding amounts. You can find an always up-to-date overview of all federal and state programmes in the federal funding database.',
      linkText: 'To the funding database'
    },
    stepsTitle: 'How the Funding Works',
    steps: [
      {
        title: '1. Submit application',
        text: 'First the funding application is submitted – always before commissioning or signing a contract. Retroactive funding is excluded.'
      },
      {
        title: '2. Implement project',
        text: 'After approval, we implement your consulting or digitalization project together.'
      },
      {
        title: '3. Receive grant',
        text: 'After completion and proof of use, the grant is paid out by the responsible authority.'
      }
    ],
    ctaTitle: "Let's discuss your fundable project",
    ctaButton: 'Request consultation',
    disclaimer:
      'As of June 2026. All information without guarantee; it does not replace funding or legal advice. Funding amounts, requirements and deadlines may change, and eligibility is decided case by case by the responsible authority. Combining several public grants for the same measure is generally excluded.'
  }
};

const programIcons = [<FaHandshake />, <FaLaptopCode />, <FaRocket />];
const stepIcons = [<FaFileSignature />, <FaCogs />, <FaEuroSign />];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};

const Funding: React.FC = () => {
  const { language } = useLanguage();
  const t = fundingContent[language];

  return (
    <FundingSection>
      <Container>
        <BackButton to="/">
          <FaArrowLeft />
          {t.backButton}
        </BackButton>

        <SectionHeader>
          <SectionTitle>{t.title}</SectionTitle>
          <SectionSubtitle>{t.subtitle}</SectionSubtitle>
        </SectionHeader>

        <Grid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {t.programs.map((program, index) => (
            <Card key={index} variants={cardVariants} $featured={index === 2}>
              <CardIcon>{programIcons[index]}</CardIcon>
              <Amount>{program.amount}</Amount>
              <AmountLabel>{program.amountLabel}</AmountLabel>
              <CardTitle>{program.title}</CardTitle>
              <CardText>{program.text}</CardText>
              <FactList>
                {program.facts.map((fact, factIndex) => (
                  <FactItem key={factIndex}>
                    <FaCheckCircle />
                    <span>{fact}</span>
                  </FactItem>
                ))}
              </FactList>
              {program.note && <Note>{program.note}</Note>}
            </Card>
          ))}
        </Grid>

        <EitherOr>
          <EitherOrIcon>
            <FaBalanceScale />
          </EitherOrIcon>
          <div>
            <EitherOrTitle>{t.eitherOr.title}</EitherOrTitle>
            <EitherOrText>{t.eitherOr.text}</EitherOrText>
          </div>
        </EitherOr>

        <Band>
          <BandIcon>
            <FaMapMarkedAlt />
          </BandIcon>
          <BandBody>
            <BandTitle>{t.otherStates.title}</BandTitle>
            <BandText>{t.otherStates.text}</BandText>
          </BandBody>
          <OutlineLink
            href="https://www.foerderdatenbank.de"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t.otherStates.linkText}
            <FaExternalLinkAlt />
          </OutlineLink>
        </Band>

        <StepsHeader>{t.stepsTitle}</StepsHeader>
        <StepsGrid>
          {t.steps.map((step, index) => (
            <Step key={index}>
              <StepIcon>{stepIcons[index]}</StepIcon>
              <StepTitle>{step.title}</StepTitle>
              <StepText>{step.text}</StepText>
            </Step>
          ))}
        </StepsGrid>

        <CTA>
          <CTATitle>{t.ctaTitle}</CTATitle>
          <CTAButton to="/contact">{t.ctaButton}</CTAButton>
        </CTA>

        <Disclaimer>
          <FaInfoCircle />
          <span>{t.disclaimer}</span>
        </Disclaimer>
      </Container>
    </FundingSection>
  );
};

export default Funding;
